import type { TFunction } from 'i18next';
import type { ObjectData } from '@os/doc';
import type { Mentionable } from '../../lib/comment-types';
import { relativeTime } from '../dashboard/cards';

/** Where the editor resolves authors (server boards; local boards have none): the board, the viewer, the names. */
/** `meName`: the viewer's own display name (their stickies show it, P5e). */
/** `people`: everyone who can be assigned a task on the board (P16-5), absent where nobody can. */
/** `meAvatarKey`: my photo's key (P5g); the others' come with their names. */
export type AuthorsSource = { boardId: string; me: string; meName?: string; meAvatarKey?: string | null; resolve: (userIds: string[]) => Promise<Mentionable[]>; people?: () => Promise<Mentionable[]> };
/** `kind` picks the panel's wording: "Added by Ada", "Added by you", "Unknown author", "3 authors". */
export type AuthorsView = { label: string; color: string | null; when: string | null; kind: 'person' | 'you' | 'unknown' | 'many' };

/** The distinct `createdBy` ids of these objects, and the people assigned or who checked a task (the ones to resolve). */
export const authorIds = (objects: readonly ObjectData[]): string[] =>
  [
    ...new Set(
      objects.flatMap((o) => [
        ...(o.createdBy ? [o.createdBy] : []),
        ...(o.type === 'tasklist' ? o.items.flatMap((t) => [t.assignee, t.doneBy].filter((x): x is string => !!x)) : []),
      ]),
    ),
  ].sort();

/**
 * Who added these objects (P5d, backlog §10.5): one name (or "You", "Former member" when the id is no longer among
 * the board's readers, "Unknown author" for objects older than the feature), or "{n} authors"; when, for one object.
 * Null while the names are loading.
 */
export function authorsOf(
  objects: readonly ObjectData[],
  o: { me: string; names: ReadonlyMap<string, Mentionable> | null; t: TFunction; lng: string; now?: number },
): AuthorsView | null {
  if (!objects.length) return null;
  const ids = new Set(objects.map((x) => x.createdBy ?? ''));
  if (ids.size > 1) return { label: o.t('authors.count', { count: ids.size }), color: null, when: null, kind: 'many' };
  const by = [...ids][0]!;
  const at = objects.length === 1 ? objects[0]!.createdAt : undefined;
  const when = at !== undefined ? relativeTime(new Date(at).toISOString(), o.lng, o.now) : null;
  if (!by) return { label: o.t('authors.unknown'), color: null, when, kind: 'unknown' };
  if (by === o.me) return { label: o.t('authors.you'), color: null, when, kind: 'you' };
  if (!o.names) return null;
  const known = o.names.get(by);
  return { label: (known && known.displayName) || o.t('authors.former'), color: known?.color ?? null, when, kind: 'person' };
}
